import { LOCALE_ID, ModuleWithProviders, NgModule } from '@angular/core';
import {
  TranslateService,
  provideTranslateService,
  provideTranslateLoader,
  provideMissingTranslationHandler,
} from '@ngx-translate/core';
import { PercentPipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  TRANSLATE_HTTP_LOADER_CONFIG,
  TranslateHttpLoaderConfig,
} from '@ngx-translate/http-loader';
import * as de from '@angular/common/locales/de';
import dayjs from './utils/configured-dayjs';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import Lara from '@primeng/themes/lara';
import { NimmstaCommonsModuleConfiguration } from './nimmsta-commons-module.config';
import { DataTableModuleConfiguration } from './data-table/datatable-module.config';
import { StatusInterceptor } from './interceptors/status.interceptor';
import { AuthenticationInterceptor } from './interceptors/auth.interceptor';
import { NimmstaTimeAgoPipe } from './pipes/nimmsta-timeago.pipe';
import { MultiTranslateHttpLoader } from './utils/multi-translate-loader';
import { DateTimePipe } from './pipes/date-time.pipe';
import { TimePipe } from './pipes/time.pipe';
import { CustomMissingTranslationHandler } from './utils/missing-translation-handler';
import { OrganizationInterceptor } from './interceptors';
import { AuthService } from './services/auth.service';
import { ACTING_ORGANIZATION_ID_PROVIDER } from './services/current-organization-id.provider';
import { NIMMSTA_PRIMENG_OVERLAY_CONFIG } from './primeng-overlay.config';
import { DialogService } from 'primeng/dynamicdialog';

function getDefaultLanguage(): string {
  const userLanguage = navigator.language;
  if (userLanguage.startsWith('de')) {
    return 'de';
  }
  return 'en';
}

@NgModule({
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: StatusInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthenticationInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OrganizationInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      deps: [TranslateService],
      useFactory: (translateService: TranslateService) =>
        translateService.getBrowserCultureLang(),
    },
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {} as TranslateHttpLoaderConfig,
    },
    PercentPipe,
    NimmstaTimeAgoPipe,
    TimePipe,
    DateTimePipe,
    DialogService,
    provideTranslateService({
      loader: provideTranslateLoader(MultiTranslateHttpLoader),
      missingTranslationHandler: provideMissingTranslationHandler(
        CustomMissingTranslationHandler,
      ),
      fallbackLang: getDefaultLanguage(),
    }),
    provideAnimationsAsync(),
    providePrimeNG({
      ...NIMMSTA_PRIMENG_OVERLAY_CONFIG,
      theme: {
        preset: definePreset(Lara, {
          primitive: {
            blue: {
              50: '#f2f9fc',
              100: '#c2e4ef',
              200: '#91cfe3',
              300: '#61bad6',
              400: '#30a5ca',
              500: '#0090bd',
              600: '#007aa1',
              700: '#006584',
              800: '#004f68',
              900: '#003a4c',
            },
            green: {
              50: '#f6fbf6',
              100: '#d4ecd5',
              200: '#b2ddb4',
              300: '#90cd93',
              400: '#6ebe71',
              500: '#4caf50',
              600: '#419544',
              700: '#357b38',
              800: '#2a602c',
              900: '#1e4620',
            },
            yellow: {
              50: '#fffcf5',
              100: '#fef0cd',
              200: '#fde4a5',
              300: '#fdd87d',
              400: '#fccc55',
              500: '#fbc02d',
              600: '#d5a326',
              700: '#b08620',
              800: '#8a6a19',
              900: '#644d12',
            },
            cyan: {
              50: '#f2fcfd',
              100: '#c2eff5',
              200: '#91e2ed',
              300: '#61d5e4',
              400: '#30c9dc',
              500: '#00bcd4',
              600: '#00a0b4',
              700: '#008494',
              800: '#006775',
              900: '#004b55',
            },
            pink: {
              50: '#fef4f7',
              100: '#fac9da',
              200: '#f69ebc',
              300: '#f1749e',
              400: '#ed4981',
              500: '#e91e63',
              600: '#c61a54',
              700: '#a31545',
              800: '#801136',
              900: '#5d0c28',
            },
            indigo: {
              50: '#f5f6fb',
              100: '#d1d5ed',
              200: '#acb4df',
              300: '#8893d1',
              400: '#6372c3',
              500: '#3f51b5',
              600: '#36459a',
              700: '#2c397f',
              800: '#232d64',
              900: '#192048',
            },
            teal: {
              50: '#f2faf9',
              100: '#c2e6e2',
              200: '#91d2cc',
              300: '#61beb5',
              400: '#30aa9f',
              500: '#009688',
              600: '#008074',
              700: '#00695f',
              800: '#00534b',
              900: '#003c36',
            },
            orange: {
              50: '#fff8f2',
              100: '#fde0c2',
              200: '#fbc791',
              300: '#f9ae61',
              400: '#f79530',
              500: '#f57c00',
              600: '#d06900',
              700: '#ac5700',
              800: '#874400',
              900: '#623200',
            },
            purple: {
              50: '#faf4fb',
              100: '#e7cbec',
              200: '#d4a2dd',
              300: '#c279ce',
              400: '#af50bf',
              500: '#9c27b0',
              600: '#852196',
              700: '#6d1b7b',
              800: '#561561',
              900: '#3e1046',
            },
            red: {
              50: '#fff5f5',
              100: '#ffd1ce',
              200: '#ffada7',
              300: '#ff8980',
              400: '#ff6459',
              500: '#ff4032',
              600: '#d9362b',
              700: '#b32d23',
              800: '#8c231c',
              900: '#661a14',
            },
          },
          semantic: {
            primary: {
              50: '#f2f9fc',
              100: '#c2e4ef',
              200: '#91cfe3',
              300: '#61bad6',
              400: '#30a5ca',
              500: '#0090bd',
              600: '#007aa1',
              700: '#006584',
              800: '#004f68',
              900: '#003a4c',
            },
            focusRing: {
              width: '0.2rem',
              color: '#bfd1f6',
            },
          },
          colorScheme: {
            light: {
              primary: {
                color: '#2196f3',
              },
              highlight: {
                background: '#0090bd',
                color: '#000000',
              },
              surface: {
                0: '#ffffff',
                50: '#fafafa',
                100: '#f5f5f5',
                200: '#eeeeee',
                300: '#e0e0e0',
                400: '#bdbdbd',
                500: '#9e9e9e',
                600: '#757575',
                700: '#616161',
                800: '#424242',
                900: '#212121',
              },
            },
          },
        }),
        options: {
          darkModeSelector: 'none',
          cssLayer: {
            name: 'primeng',
            order: 'app-styles, bootstrap, primeng',
          },
        },
      },
    }),
  ],
})
export class NimmstaCommonsModule {
  constructor() {
    // Set dayjs locale based on default language
    if (getDefaultLanguage() === 'de') {
      dayjs.locale('de');
    } else {
      dayjs.locale('en');
    }
  }

  static forRoot(
    configuration: NimmstaCommonsModuleConfiguration,
  ): ModuleWithProviders<NimmstaCommonsModule> {
    registerLocaleData(de.default);
    return {
      ngModule: NimmstaCommonsModule,
      providers: [
        { provide: NimmstaCommonsModuleConfiguration, useValue: configuration },
        {
          provide: DataTableModuleConfiguration,
          useValue: {
            defaultPageSizeOptions: configuration.defaultPageSizeOptions,
            defaultPageSize: configuration.defaultPageSize,
            apiUrls: configuration.apiUrls,
          },
        },
        {
          provide: ACTING_ORGANIZATION_ID_PROVIDER,
          deps: [AuthService],
          useFactory: (authService: AuthService) => () =>
            authService.getActingOrganizationId(),
        },
      ],
    };
  }
}
